/* Sample-product minimum enforcement.
 *
 * Two sample products can only be bought in minimum quantities:
 *   8739809820928  Sample Oils    (bottles) — minimum 3
 *   8785697702144  Sample Packets           — minimum 6
 *
 * Whenever the cart changes we re-read the authoritative cart and, if a group is
 * present but below its minimum, remove every line of that group in one request.
 * Checkout is blocked for the duration so a customer can't slip through while
 * the check is in flight.
 *
 * The cart UI (Rebuy smart cart, cart page) repaints optimistically before
 * Shopify has finished processing a change, so a single read of /cart.js can see
 * a stale cart and wrongly conclude the minimum is still met. That was why
 * removals appeared not to fire. We now wait for the cart to settle — read until
 * two consecutive responses agree — before judging it.
 */
(function () {
  'use strict';

  var SAMPLE_GROUPS = [
    { productId: 8739809820928, name: 'bottles', min: 3 },
    { productId: 8785697702144, name: 'packets', min: 6 }
  ];

  var CHANGE_DEBOUNCE_MS = 250;
  var SETTLE_INTERVAL_MS = 300;
  /* Worst case a customer waits SETTLE_MAX_READS * SETTLE_INTERVAL_MS * MAX_PASSES
     with checkout held, so keep the product of these modest. */
  var SETTLE_MAX_READS = 4;
  var MAX_PASSES = 2;
  var NODE_RECHECK_MS = 1000;

  /* Removing lines is followed by a reload so the cart UI can't show a stale
     cart. If something re-adds them (a gift/bundle app, a selling-plan line),
     that would loop forever, so cap the reloads per session. */
  var RELOAD_GUARD_KEY = 'sampleMinimumReloads';
  var MAX_RELOADS = 2;

  /* Checkout controls this theme renders itself. Accelerated buttons (Shop Pay,
     PayPal) render in third-party iframes and cannot be click-intercepted, so
     they are neutralised via their wrapper in 1custom.css instead. */
  var CHECKOUT_CONTROLS = [
    '.rebuy-cart__checkout-button',
    '[name="checkout"]',
    '#cro-checkout-btn',
    'a[href$="/checkout"]'
  ].join(', ');

  var BLOCKED_CLASS = 'sample-minimum-validating';

  var running = false;
  var rerunRequested = false;
  var debounceTimer = null;
  var pendingOptions = null;
  var observedNode = null;
  var observer = null;
  var parentObserver = null;
  var nodeWatchTimer = null;

  /* Diagnostics are off by default so a normal page view stays quiet. Enable with
     ?sample_debug=1 on any URL, or localStorage.sampleMinimumDebug = '1' to keep
     it on across a session. Errors are never gated. */
  var debugEnabled = (function () {
    try {
      if (window.location.search.indexOf('sample_debug=1') !== -1) return true;
      return window.localStorage.getItem('sampleMinimumDebug') === '1';
    } catch (error) {
      return false;
    }
  })();

  function log() {
    if (!debugEnabled) return;
    var args = ['TEST:: [sample-minimum]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /* ---------- checkout blocking ---------- */

  function swallowCheckoutClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest(CHECKOUT_CONTROLS)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    log('blocked a checkout attempt while the cart was being validated');
  }

  /* Must not catch the product form at /cart/add — matching "/cart" loosely
     would block add-to-cart on every product page. Only the cart form itself
     and a direct checkout form qualify. */
  function isCheckoutForm(form) {
    var action = (form.getAttribute('action') || '').split('?')[0].replace(/\/+$/, '');
    return /\/cart$/.test(action) || /\/checkout$/.test(action);
  }

  function swallowCartSubmit(event) {
    var form = event.target;
    if (!form || typeof form.getAttribute !== 'function') return;
    if (!isCheckoutForm(form)) return;

    /* The cart form carries Update as well as Checkout. Where the browser tells
       us which button submitted, let everything but checkout through. */
    var submitter = event.submitter;
    if (submitter && typeof submitter.matches === 'function' && !submitter.matches(CHECKOUT_CONTROLS)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    log('blocked a cart form submit while the cart was being validated');
  }

  function blockCheckout() {
    document.addEventListener('click', swallowCheckoutClick, true);
    document.addEventListener('submit', swallowCartSubmit, true);
    if (document.body) document.body.classList.add(BLOCKED_CLASS);
  }

  function unblockCheckout() {
    document.removeEventListener('click', swallowCheckoutClick, true);
    document.removeEventListener('submit', swallowCartSubmit, true);
    if (document.body) document.body.classList.remove(BLOCKED_CLASS);
  }

  /* ---------- cart access ---------- */

  function reloadCount() {
    try {
      return parseInt(window.sessionStorage.getItem(RELOAD_GUARD_KEY), 10) || 0;
    } catch (error) {
      return 0;
    }
  }

  function setReloadCount(value) {
    try {
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(value));
    } catch (error) {
      /* Private mode / storage disabled — worst case we lose the cap. */
    }
  }

  function clearReloadCount() {
    try {
      window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch (error) {
      /* nothing to do */
    }
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function readCart() {
    return fetch(cartRoot() + 'cart.js', {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('cart.js responded ' + response.status);
      return response.json();
    });
  }

  function cartSignature(cart) {
    return (cart.items || [])
      .map(function (item) {
        return item.variant_id + 'x' + item.quantity;
      })
      .join('|');
  }

  /* Read until two consecutive responses match, so we judge a cart that Shopify
     has finished writing rather than one mid-update. */
  function readSettledCart() {
    var previousSignature = null;

    function attempt(readsLeft) {
      return readCart().then(function (cart) {
        var signature = cartSignature(cart);
        if (previousSignature !== null && signature === previousSignature) return cart;
        previousSignature = signature;
        if (readsLeft <= 1) return cart;
        return delay(SETTLE_INTERVAL_MS).then(function () {
          return attempt(readsLeft - 1);
        });
      });
    }

    return attempt(SETTLE_MAX_READS);
  }

  /* Collect every variant that has to go, across all groups, so a single request
     fixes the whole cart instead of two requests racing each other. */
  function planRemovals(cart) {
    var updates = {};
    var violations = [];

    SAMPLE_GROUPS.forEach(function (group) {
      var lines = (cart.items || []).filter(function (item) {
        return String(item.product_id) === String(group.productId);
      });

      var quantity = lines.reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0);

      if (quantity > 0 && quantity < group.min) {
        violations.push(group.name + ': ' + quantity + ' of ' + group.min);
        lines.forEach(function (item) {
          updates[String(item.variant_id)] = 0;
        });
      }
    });

    return { updates: updates, violations: violations, lineCount: Object.keys(updates).length };
  }

  function applyRemovals(updates) {
    return fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart/update.js responded ' + response.status);
      return response.json();
    });
  }

  /* ---------- orchestration ---------- */

  /* options.settle          — poll until the cart stops changing. Needed after a
                               cart mutation, pointless on a fresh page load.
     options.blockImmediately — hold checkout for the whole check, not just the
                               removal. Used for cart changes, so a customer
                               can't check out during the window. On page load we
                               block lazily instead, otherwise every page view
                               would disable checkout for a second. */
  function validate(options) {
    options = options || {};

    if (running) {
      rerunRequested = true;
      return Promise.resolve();
    }

    running = true;

    var blocked = false;

    function ensureBlocked() {
      if (blocked) return;
      blockCheckout();
      blocked = true;
    }

    if (options.blockImmediately) ensureBlocked();

    var removedAnything = false;
    var read = options.settle ? readSettledCart : readCart;

    function pass(passesLeft) {
      return read().then(function (cart) {
        var plan = planRemovals(cart);
        if (plan.lineCount === 0) return null;

        ensureBlocked();
        log('below minimum (' + plan.violations.join(', ') + ') — removing ' + plan.lineCount + ' line(s)');

        return applyRemovals(plan.updates).then(function () {
          /* Only now is the removal real. Setting this before the request would
             make a failed update (422 from a bundle app, offline) trigger a
             pointless reload of an unchanged cart. */
          removedAnything = true;

          /* The cart may have changed again while we were writing, so re-check
             rather than assuming one pass settles it. Re-read with settling even
             if this run started without it — we just mutated the cart. */
          read = readSettledCart;
          if (passesLeft <= 1) return null;
          return pass(passesLeft - 1);
        });
      });
    }

    return pass(MAX_PASSES)
      .catch(function (error) {
        console.error('TEST:: [sample-minimum] validation failed', error);
      })
      .then(function () {
        running = false;

        if (removedAnything) {
          var reloads = reloadCount();

          if (reloads >= MAX_RELOADS) {
            /* Something keeps putting these lines back. Stop reloading rather
               than trap the customer in a refresh loop. */
            console.error(
              'TEST:: [sample-minimum] lines were removed but keep returning after ' +
                reloads +
                ' reloads — leaving the cart alone'
            );
            unblockCheckout();
            return;
          }

          /* Rebuy exposes no dependable refresh hook, so reload to guarantee the
             cart UI matches the server. Checkout stays blocked until it lands. */
          setReloadCount(reloads + 1);
          log('reloading so the cart UI matches the server');
          window.location.reload();
          return;
        }

        /* A clean pass means the cycle resolved; forget the reload budget. */
        clearReloadCount();

        if (blocked) unblockCheckout();

        if (rerunRequested) {
          rerunRequested = false;
          /* Something changed mid-flight, so treat it as a cart change. */
          scheduleValidation({ settle: true, blockImmediately: true });
        }
      });
  }

  function scheduleValidation(options) {
    options = options || {};

    /* Coalesced triggers take the stronger option of the batch. */
    pendingOptions = {
      settle: Boolean((pendingOptions && pendingOptions.settle) || options.settle),
      blockImmediately: Boolean(
        (pendingOptions && pendingOptions.blockImmediately) || options.blockImmediately
      )
    };

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      var queued = pendingOptions || {};
      pendingOptions = null;
      validate(queued);
    }, CHANGE_DEBOUNCE_MS);
  }

  function onCartChanged() {
    scheduleValidation({ settle: true, blockImmediately: true });
  }

  /* ---------- change detection ---------- */

  /* Polling only runs while we have nothing attached. Once attached, the parent
     observer tells us if the drawer is replaced, so there's no steady-state timer. */
  function startNodeWatch() {
    if (nodeWatchTimer) return;
    nodeWatchTimer = setInterval(watchSmartCart, NODE_RECHECK_MS);
  }

  function stopNodeWatch() {
    if (!nodeWatchTimer) return;
    clearInterval(nodeWatchTimer);
    nodeWatchTimer = null;
  }

  function watchSmartCart() {
    var node = document.querySelector('[data-smartcart-items]');

    if (!node) {
      if (observedNode) {
        /* The drawer was torn down; drop the stale observers so we re-attach when
           it comes back. */
        if (observer) observer.disconnect();
        if (parentObserver) parentObserver.disconnect();
        observedNode = null;
      }
      startNodeWatch();
      return;
    }

    if (node === observedNode && document.contains(observedNode)) {
      stopNodeWatch();
      return;
    }

    if (observer) observer.disconnect();
    if (parentObserver) parentObserver.disconnect();

    /* Wrapped: MutationObserver passes (mutations, observer), which would
       otherwise arrive as the options argument. */
    observer = new MutationObserver(onCartChanged);
    observer.observe(node, { childList: true, subtree: true, characterData: true });

    /* An observer on the node itself never fires when the node is removed from
       its parent, so watch the parent to notice a replaced drawer. */
    if (node.parentNode) {
      parentObserver = new MutationObserver(function () {
        watchSmartCart();
      });
      parentObserver.observe(node.parentNode, { childList: true });
    }

    observedNode = node;
    stopNodeWatch();
    log('watching the smart cart for changes');
  }

  function watchCartPage() {
    var section = document.querySelector('[data-section-id="cart-template"]');
    if (!section) return;
    /* Wrapped so the event object isn't read as the options argument. */
    section.addEventListener('cartUpdated', onCartChanged);
    log('watching the cart page for changes');
  }

  /* The header renders cart.item_count server-side, so an empty cart can be ruled
     out without spending a request. If the element is missing we fall through and
     read, rather than assuming. */
  function renderedCartIsEmpty() {
    var el = document.querySelector('[data-cart-count]');
    if (!el) return false;
    var count = parseInt((el.textContent || '').trim(), 10);
    return count === 0;
  }

  function init() {
    watchCartPage();
    watchSmartCart();

    /* Catch a cart that was already invalid on arrival, e.g. after a back
       navigation or a checkout bounce. One read is enough here — nothing is
       mid-write on a fresh load — and checkout is only held if we actually find
       a violation, so a normal page view is untouched. */
    if (renderedCartIsEmpty()) {
      /* An empty cart is a resolved cart, so release the reload budget here too.
         Skipping straight past validate() would otherwise let the counter
         accumulate across separate removals until it refused to reload. */
      clearReloadCount();
      log('cart renders as empty; skipping the arrival check');
      return;
    }

    scheduleValidation({ settle: false, blockImmediately: false });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
